import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Space, Button, type MenuProps } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import type { TCreateTripTaskReq } from 'api/swagger';
import { useCreateTripTask } from 'api/trip-task/use-create-trip-task';
import { useRoleGuard } from 'shared/hooks/use-role-guard';
import { useTripTaskStore } from 'shared/stores';
import { isTripStarted } from 'shared/utils/trips/is-trip-started';
import { UserRole } from 'types/user';
import { TripAddCargoModal } from 'components/trips/trip-add-cargo-modal';
import { TripCreateTaskModal } from 'components/trips/trip-create-task-modal';
import { TripTrailerSwapModal } from 'components/trips/trip-trailer-swap-modal';
import type { ITripTaskActionsProps } from './types';

export const TripTaskActions = (props: ITripTaskActionsProps) => {
  const { tripId, tripStatus } = props;
  const { t } = useTranslation('trip');
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isAddCargoModalOpen, setIsAddCargoModalOpen] = useState(false);
  const [isTrailerSwapModalOpen, setIsTrailerSwapModalOpen] = useState(false);

  const isAllowAddCargo = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.transport_manager]);

  const { cargoTrips: selectedCargoTrips, clearCargoTrips } = useTripTaskStore();
  const { mutateAsync: createTripTask, isLoading } = useCreateTripTask(tripId);

  const isCreateTripTaskDisabled = selectedCargoTrips.length === 0;
  const selectedCargoIds = selectedCargoTrips.map((cargoTrip) => cargoTrip.id);

  const handleOpenCreateTask = () => {
    if (isCreateTripTaskDisabled) return;

    setIsCreateTaskModalOpen(true);
  };
  const handleCloseCreateTask = () => setIsCreateTaskModalOpen(false);

  const handleOpenAddCargo = () => setIsAddCargoModalOpen(true);
  const handleCloseAddCargo = () => setIsAddCargoModalOpen(false);

  const handleOpenTrailerSwap = () => setIsTrailerSwapModalOpen(true);
  const handleCloseTrailerSwap = () => setIsTrailerSwapModalOpen(false);

  const handleSubmitCreateTask = async (state: TCreateTripTaskReq) => {
    await createTripTask(state);
    handleCloseCreateTask();
  };

  const handleTrailerSwapTaskCreate = async (data: TCreateTripTaskReq) => {
    await createTripTask(data);
    handleCloseTrailerSwap();
  };

  const handleSuccessAddCargo = () => {
    clearCargoTrips();
  };

  const isTripStartedOrDone = isTripStarted(tripStatus);

  const menuItems: MenuProps['items'] = [
    ...(isAllowAddCargo
      ? [
          {
            key: 'add-cargo',
            label: t('add_cargo_to_trip'),
            onClick: handleOpenAddCargo,
            disabled: isTripStartedOrDone,
          },
        ]
      : []),
    {
      key: 'trailer-swap',
      label: t('trailer_swap'),
      onClick: handleOpenTrailerSwap,
    },
  ];

  return (
    <>
      <Space.Compact>
        <Button
          type='primary'
          style={{ cursor: isCreateTripTaskDisabled ? 'not-allowed' : 'pointer' }}
          onClick={handleOpenCreateTask}>
          {t('create_tasks')}
        </Button>
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement='bottomRight'>
          <Button type='primary' icon={<EllipsisOutlined />} />
        </Dropdown>
      </Space.Compact>
      <TripCreateTaskModal
        open={isCreateTaskModalOpen}
        isPending={isLoading}
        onSubmit={handleSubmitCreateTask}
        onCancel={handleCloseCreateTask}
      />
      <TripAddCargoModal
        open={isAddCargoModalOpen}
        tripId={tripId}
        cargoTripIds={selectedCargoIds}
        onCancel={handleCloseAddCargo}
        onSuccess={handleSuccessAddCargo}
      />
      <TripTrailerSwapModal
        open={isTrailerSwapModalOpen}
        onCancel={handleCloseTrailerSwap}
        onSubmit={handleTrailerSwapTaskCreate}
      />
    </>
  );
};
